import InboundActions from '@intercom/embercom-composer/mixins/inbound-actions';
import { computed } from '@ember/object';
import { ternary } from '@intercom/pulse/lib/computed-properties';
import Constants from '@intercom/embercom-composer/lib/constants';
import latinize from 'latinize';
import DidChangeAttrs from '@intercom/embercom-composer/mixins/did-change-attrs';
import Triejs from 'triejs';
import { addEventListener, runDisposables } from 'ember-lifeline';
import normalizeQuotes from '@intercom/embercom-composer/lib/normalize-quotes';
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import $ from 'jquery';
import { notEmpty } from '@ember/object/computed';
import { schedule } from '@ember/runloop';
import Em from 'ember';
function getSortFunction(sortKeys) {
  return function (a, b) {
    let comparisonResult = 0;
    for (let sortKey of sortKeys) {
      let left = sortKey.reverse ? b : a;
      let right = sortKey.reverse ? a : b;
      let leftValue = left.get(sortKey.key);
      let rightValue = right.get(sortKey.key);
      if (typeof leftValue === 'string' && typeof rightValue === 'string') {
        comparisonResult = leftValue.localeCompare(rightValue);
      } else if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        comparisonResult = leftValue - rightValue;
      }
      if (comparisonResult !== 0) {
        return comparisonResult;
      }
    }
    return 0;
  };
}
export default Em.Component.extend(InboundActions, DidChangeAttrs, {
  tagName: 'span',
  verticalPadding: 8,
  triggerWidth: 1,
  style: Em.computed('_style', function () {
    return sanitizeHtml(this.get('_style'));
  }),
  _style: computed('left', 'triggerWidth', 'top', 'verticalPadding', 'visibility', function () {
    return `position:fixed;left: calc(${this.left}px - ${this.triggerWidth}px);bottom: calc(100% - ${this.top}px + ${this.verticalPadding}px);visibility:${this.visibility};`;
  }),
  minimumQueryLength: 0,
  inViewport: true,
  visibility: ternary('inViewport', 'visible', 'hidden'),
  top: 0,
  left: 0,
  hasBeenShownYet: false,
  didChangeAttrsConfig: {
    attrs: ['textBeforeCursor']
  },
  init() {
    this._super(...arguments);
    this.set('sortKeys', []);
  },
  didChangeAttrs(changes) {
    this._super(...arguments);
    if (changes.textBeforeCursor) {
      this.showOrHide();
    }
  },
  items: Em.computed('selectableItems.@each.{id,name,summary}', function () {
    let sortKeys = this.get('sortKeys');
    let trie = new Triejs({
      sort() {
        if (sortKeys.length) {
          this.sort(getSortFunction(sortKeys));
        }
      }
    });
    this.get('selectableItems').forEach(function (item) {
      item.set('isHighlighted', false);
      trie.add(normalizeQuotes(latinize(item.get('key'))), item);
    });
    return trie;
  }),
  willDestroyElement() {
    this._super(...arguments);
    runDisposables(this);
  },
  _setupPositionEventHandlers() {
    addEventListener(this, $(this.element).scrollParent()[0], 'scroll', this.setPosition);
    let closestComposerEditor = $(this.element).closest('.embercom-composer').find('.embercom-composer-editor')[0];
    addEventListener(this, closestComposerEditor, 'scroll', this.setPosition);
    addEventListener(this, window, 'resize', this.setPosition);
  },
  showOrHide() {
    if (this.get('query.length') > this.get('minimumQueryLength') && this.get('hasMatchingItems')) {
      if (!this.get('hasBeenShownYet')) {
        this._setupPositionEventHandlers();
        this.set('hasBeenShownYet', true);
      }
      this.set('isVisible', true);
      this.get('visibilityChanged')(true);
      this.setPosition();
    } else {
      this.set('isVisible', false);
      this.get('visibilityChanged')(false);
    }
  },
  setPosition() {
    if (this.get('hasMatchingItems')) {
      let coords = this.get('getTriggerCoords')(this.get('triggerCharacter'));
      let closestCoords = coords.closestCoords;
      let editorCoords = coords.editorCoords;
      if (closestCoords) {
        this.setProperties({
          left: closestCoords.x,
          top: closestCoords.y
        });
        if (closestCoords.y < editorCoords.top || closestCoords.y > editorCoords.bottom) {
          this.set('inViewport', false);
        } else {
          this.set('inViewport', true);
        }
      }
    }
  },
  query: Em.computed('textBeforeCursor', function () {
    let beforeCursor = this.get('textBeforeCursor');
    if (!beforeCursor) {
      return '';
    }
    let lastTriggerIndex = beforeCursor.lastIndexOf(this.get('triggerCharacter'));
    if (this._isValidTrigger(lastTriggerIndex, beforeCursor)) {
      let query = beforeCursor.slice(lastTriggerIndex).toLowerCase();
      return normalizeQuotes(latinize(query));
    }
    return '';
  }),
  _isValidTrigger(index, text) {
    return index !== -1 && (index === 0 || text.charAt(index - 1) === ' ' || text.charAt(index - 1) === '\n' || text.charAt(index - 1) === Constants.NON_BREAKING_SPACE);
  },
  hasMatchingItems: notEmpty('matchingItems'),
  highlightedItem: Em.computed('matchingItems', 'matchingItems.@each.isHighlighted', function () {
    return this.get('matchingItems').findBy('isHighlighted');
  }),
  highlightedItemIndex: Em.computed('matchingItems', 'matchingItems.@each.isHighlighted', function () {
    return this.get('matchingItems').indexOf(this.get('highlightedItem'));
  }),
  nextEnabledItemIndex: Em.computed('matchingItems', 'highlightedItemIndex', function () {
    let startIndex = 0;
    if (this.highlightedItemIndex !== -1) {
      startIndex = this.highlightedItemIndex + 1;
    }
    let totalItems = this.matchingItems.length;
    for (let i = startIndex; i < totalItems; i++) {
      let item = this.matchingItems.objectAt(i);
      if (!item.isDisabled) {
        return i;
      }
    }
    return this.highlightedItemIndex;
  }),
  previousEnabledItemIndex: Em.computed('matchingItems', 'highlightedItemIndex', function () {
    if (this.highlightedItemIndex === -1) {
      return this.nextEnabledItemIndex;
    }
    for (let i = this.highlightedItemIndex - 1; i >= 0; i--) {
      let item = this.matchingItems.objectAt(i);
      if (item && !item.isDisabled) {
        return i;
      }
    }
    return this.highlightedItemIndex;
  }),
  clearTypedContent() {
    let composerState = this.get('composerState');
    let selection = composerState.get('selection');
    let beforeCursor = this.get('textBeforeCursor');
    let typeaheadStartIndex = beforeCursor.lastIndexOf(this.get('triggerCharacter'));
    composerState.setSelection(selection.get('startBlockIndex'), selection.get('startItemIndex'), typeaheadStartIndex, selection.get('startBlockIndex'), selection.get('startItemIndex'), selection.get('startPositionIndex'));
    composerState.backspace({
      noCheckpoint: true
    });
  },
  insertContentAndApplyEntity(content, entityType, entityProperties) {
    let composerState = this.get('composerState');
    let selection = this.get('composerState.selection');
    let contentStart = selection.get('startPositionIndex');
    composerState.insertCharacters(content);
    composerState.setSelection(selection.get('startBlockIndex'), selection.get('startItemIndex'), contentStart, selection.get('startBlockIndex'), selection.get('startItemIndex'), selection.get('startPositionIndex'));
    let commandResult = composerState[entityType](entityProperties);
    composerState.setSelection(selection.get('startBlockIndex'), selection.get('startItemIndex'), selection.get('endPositionIndex'), selection.get('startBlockIndex'), selection.get('startItemIndex'), selection.get('endPositionIndex'));
    composerState.insertCharacter(' ');
    return commandResult;
  },
  scrollToActiveItem() {
    let $dropdown = $('.dropdown', this.element);
    let activeElement = $('.o__selected', this.element).position();
    if (!(activeElement && $dropdown)) {
      return;
    }
    let currentScrollTop = $dropdown.scrollTop();
    let padding = 50;
    if (activeElement.top > $dropdown.height()) {
      $dropdown.scrollTop(currentScrollTop + activeElement.top - $dropdown.height() + padding);
    } else if (currentScrollTop > 0 && activeElement.top < padding) {
      $dropdown.scrollTop(currentScrollTop + activeElement.top - padding);
    }
  },
  actions: {
    highlightItem(item) {
      if (item) {
        this.get('matchingItems').setEach('isHighlighted', false);
        if (!item.isDisabled) {
          item.set('isHighlighted', true);
        }
      }
    },
    onUp() {
      let matchingItems = this.get('matchingItems');
      if (matchingItems.length === 0) {
        return;
      }
      let previousIndex = this.previousEnabledItemIndex;
      if (previousIndex < 0) {
        previousIndex = 0;
      }
      let highlightedItem = this.get('highlightedItem');
      if (highlightedItem) {
        highlightedItem.set('isHighlighted', false);
      }
      let previousItem = matchingItems.objectAt(previousIndex);
      if (previousItem && !previousItem.isDisabled) {
        previousItem.set('isHighlighted', true);
      }
      schedule('afterRender', this, this.scrollToActiveItem);
    },
    onDown() {
      let matchingItems = this.get('matchingItems');
      if (matchingItems.length === 0) {
        return;
      }
      let highlightedItem = this.get('highlightedItem');
      let nextIndex = this.nextEnabledItemIndex;
      let lastIndex = matchingItems.get('length') - 1;
      if (nextIndex > lastIndex) {
        nextIndex = lastIndex;
      }
      if (highlightedItem) {
        highlightedItem.set('isHighlighted', false);
      }
      let nextItem = matchingItems.objectAt(nextIndex);
      if (nextItem && !nextItem.isDisabled) {
        nextItem.set('isHighlighted', true);
      }
      schedule('afterRender', this, this.scrollToActiveItem);
    },
    onTab() {
      let item = this.get('highlightedItem');
      if (item) {
        this.send('selectItem', item);
      }
    },
    onEsc() {
      this.set('isVisible', false);
    },
    onEnter() {
      let item = this.get('highlightedItem');
      if (item) {
        this.send('selectItem', item);
      }
    },
    selectItem(item) {
      if (item.isDisabled) {
        return;
      }
      let markerId = this.insertItem(item);
      this.refresh();
      this.typeAheadWasInserted?.({
        type: this.get('type'),
        value: item,
        markerId
      });
    }
  }
});